import { computed } from 'vue'
import { useShippingLine } from '~/compositions/useShippingLines'
import type { Appointment } from '~/models/appointments'
import type { ReturnRule } from '~/models/returnRules'
import type {
  ContainerType,
  GETShippingLine,
  ShippingLine,
  TerminalName,
} from '~/services/apiClient'
import { useReturnRulesLatest } from '~/stores/returnRulesLatest'

interface HasLineAndType {
  shipping_line: ShippingLine
  container_type: ContainerType
}

interface UseSwapEmptyRulesProps {
  appointment: Appointment
  showReturnRules: boolean
  shippingLine?: ShippingLine
  containerType?: ContainerType
  terminal: Ref<TerminalName>
  form: {
    shipping_line?: ShippingLine
    container_type?: ContainerType
  }
}

export function useSwapEmptyRules(props: UseSwapEmptyRulesProps) {
  const latestReturnRulesStore = useReturnRulesLatest()
  const { getShippingLine } = useShippingLine()

  const receivingOptions = computed((): ReturnRule[] => {
    if (!props.showReturnRules) {
      return []
    }
    if (latestReturnRulesStore.loading) {
      return []
    }
    const returnRules = latestReturnRulesStore.lookupRulesForTerminal(
      props.appointment.terminal,
      props.appointment.shiftDate,
      props.appointment.shift
    )
    return returnRules.filter((rule) => rule.can_dual)
  })

  const receivingOptionsToShowContainersFor = computed((): HasLineAndType[] => {
    if (props.shippingLine && props.containerType) {
      return [
        {
          shipping_line: props.shippingLine,
          container_type: props.containerType,
        },
      ]
    } else {
      return receivingOptions.value
    }
  })

  const receivingShippingLines = computed((): GETShippingLine[] => {
    const shippingLines = new Set<ShippingLine>()
    for (const rule of receivingOptions.value) {
      if (rule.can_dual) {
        shippingLines.add(rule.shipping_line)
      }
    }
    const shippingLineInfos: GETShippingLine[] = []
    for (const line of shippingLines) {
      const shippingLine = getShippingLine(line)
      if (shippingLine) {
        shippingLineInfos.push(shippingLine)
      }
    }
    return shippingLineInfos
  })

  const selectedShippingLine = computed(() => {
    if (props.form.shipping_line) {
      return getShippingLine(props.form.shipping_line)
    }
    return null
  })

  const receivingRule = computed(() => {
    if (props.form.shipping_line && props.form.container_type) {
      return latestReturnRulesStore.lookupRule(
        props.form.shipping_line,
        props.form.container_type,
        props.terminal.value,
        props.appointment.shiftDate,
        props.appointment.shift
      )
    }
    return null
  })

  function getShippingLineName(line: ShippingLine): string {
    const lineInfo = getShippingLine(line)
    if (lineInfo) {
      return lineInfo.long_desc
    } else {
      return line
    }
  }

  return {
    // Computed
    receivingOptions,
    receivingOptionsToShowContainersFor,
    receivingShippingLines,
    selectedShippingLine,
    receivingRule,

    // Methods
    getShippingLineName,
  }
}
